import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { 
  Table, Card, Tag, Space, Button, Select, DatePicker, Statistic, Row, Col, Badge 
} from 'antd';
import {
  DollarOutlined,
  SearchOutlined,
  ReloadOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  FileTextOutlined,
  CreditCardOutlined
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { fetchPayments, fetchPaymentStats, Payment } from '../lib/api';
import dayjs from 'dayjs';

const { RangePicker } = DatePicker;

const Payments: React.FC = () => {
  const navigate = useNavigate();
  const [filters, setFilters] = useState({
    provider: undefined as string | undefined,
    status_filter: undefined as string | undefined,
    type_filter: undefined as string | undefined,
    credited_only: undefined as boolean | undefined,
    limit: 50,
    skip: 0,
  });
  const [dateRange, setDateRange] = useState<[dayjs.Dayjs | null, dayjs.Dayjs | null]>([null, null]);

  // Fetch payments
  const { data: paymentsData, isLoading, refetch } = useQuery({
    queryKey: ['payments', filters, dateRange],
    queryFn: () => fetchPayments({
      ...filters,
      start_date: dateRange[0]?.format('YYYY-MM-DD'),
      end_date: dateRange[1]?.format('YYYY-MM-DD'),
    }),
  });

  // Fetch stats
  const { data: statsData } = useQuery({
    queryKey: ['payment-stats', dateRange],
    queryFn: () => fetchPaymentStats({
      start_date: dateRange[0]?.format('YYYY-MM-DD'),
      end_date: dateRange[1]?.format('YYYY-MM-DD'),
    }),
  });

  const columns = [
    {
      title: 'Order ID',
      dataIndex: 'order_id',
      key: 'order_id',
      width: 250,
      render: (text: string) => <span style={{ fontFamily: 'monospace', fontSize: '12px' }}>{text}</span>,
    },
    {
      title: 'Provider',
      dataIndex: 'provider',
      key: 'provider',
      render: (provider: string) => {
        const colors = { yekpay: 'blue', nowpayments: 'green' };
        const icons = { yekpay: <CreditCardOutlined />, nowpayments: <DollarOutlined /> };
        return (
          <Tag color={colors[provider as keyof typeof colors]} icon={icons[provider as keyof typeof icons]}>
            {provider.toUpperCase()}
          </Tag>
        );
      },
    },
    {
      title: 'Type',
      dataIndex: 'type',
      key: 'type',
      render: (type: string) => (
        <Tag color="purple">{type.replace('_', ' ').toUpperCase()}</Tag>
      ),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (status: string) => {
        const colors: Record<string, string> = {
          created: 'default',
          finished: 'success',
          failed: 'error',
          pending: 'warning',
        };
        return <Tag color={colors[status] || 'default'}>{status.toUpperCase()}</Tag>;
      },
    },
    {
      title: 'Credited',
      dataIndex: 'credited',
      key: 'credited',
      render: (credited: boolean) => (
        credited ? (
          <Tag color="success" icon={<CheckCircleOutlined />}>✓ Yes</Tag>
        ) : (
          <Tag icon={<ClockCircleOutlined />}>Pending</Tag>
        )
      ),
    },
    {
      title: 'Amount',
      key: 'amount',
      render: (_: any, record: Payment) => (
        <Space direction="vertical" size={0}>
          <span><strong>{record.price_amount} {record.price_currency}</strong></span>
          {record.amount && <span style={{ fontSize: '12px', color: '#888' }}>{record.amount} ZAR</span>}
        </Space>
      ),
    },
    {
      title: 'Created',
      dataIndex: 'created_at',
      key: 'created_at',
      render: (date: string) => dayjs(date).format('MMM D, HH:mm'),
    },
    {
      title: 'Processed',
      dataIndex: 'processed_at',
      key: 'processed_at',
      render: (date?: string) => date ? dayjs(date).format('MMM D, HH:mm') : '-',
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_: any, record: Payment) => (
        <Space>
          {record.invoice_id && (
            <Button 
              type="link" 
              size="small"
              icon={<FileTextOutlined />}
              onClick={() => navigate(`/invoices/${record.invoice_id}`)}
            >
              Invoice
            </Button>
          )}
        </Space>
      ),
    },
  ];

  return (
    <div style={{ padding: '24px', height: '100%', overflow: 'auto' }}>
      <h1>Payment Management</h1>

      {/* Stats Cards */}
      {statsData && (
        <Row gutter={16} style={{ marginBottom: '24px' }}>
          <Col span={4}>
            <Card>
              <Statistic
                title="Total Payments"
                value={statsData.payments.total}
                prefix={<DollarOutlined />}
              />
            </Card>
          </Col>
          <Col span={4}>
            <Card>
              <Statistic
                title="Credited"
                value={statsData.payments.credited}
                prefix={<CheckCircleOutlined />}
                valueStyle={{ color: '#3f8600' }}
              />
            </Card>
          </Col>
          <Col span={4}>
            <Card>
              <Statistic
                title="Uncredited"
                value={statsData.payments.uncredited}
                prefix={<ClockCircleOutlined />}
                valueStyle={{ color: '#faad14' }}
              />
            </Card>
          </Col>
          <Col span={4}>
            <Card>
              <Statistic
                title="Yekpay"
                value={statsData.payments.by_provider.yekpay}
                prefix={<CreditCardOutlined />}
                valueStyle={{ color: '#1890ff' }}
              />
            </Card>
          </Col>
          <Col span={4}>
            <Card>
              <Statistic
                title="NOWPayments"
                value={statsData.payments.by_provider.nowpayments}
                prefix={<DollarOutlined />}
                valueStyle={{ color: '#52c41a' }}
              />
            </Card>
          </Col>
          <Col span={4}>
            <Card>
              <Statistic
                title="Failed"
                value={statsData.payments.by_status.failed}
                valueStyle={{ color: '#cf1322' }}
              />
            </Card>
          </Col>
        </Row>
      )}

      {/* Filters */}
      <Card style={{ marginBottom: '24px' }}>
        <Space wrap>
          <Select
            placeholder="Provider"
            style={{ width: 150 }}
            allowClear
            value={filters.provider}
            onChange={(value) => setFilters({ ...filters, provider: value, skip: 0 })}
            options={[
              { label: 'Yekpay', value: 'yekpay' },
              { label: 'NOWPayments', value: 'nowpayments' },
            ]}
          />
          <Select
            placeholder="Status"
            style={{ width: 150 }}
            allowClear
            value={filters.status_filter}
            onChange={(value) => setFilters({ ...filters, status_filter: value, skip: 0 })}
            options={[
              { label: 'Created', value: 'created' },
              { label: 'Finished', value: 'finished' },
              { label: 'Failed', value: 'failed' },
            ]}
          />
          <Select
            placeholder="Type"
            style={{ width: 180 }}
            allowClear
            value={filters.type_filter}
            onChange={(value) => setFilters({ ...filters, type_filter: value, skip: 0 })}
            options={[
              { label: 'Subscription', value: 'subscription' },
              { label: 'Wallet Top-up', value: 'wallet_topup' },
            ]}
          />
          <Select
            placeholder="Credited"
            style={{ width: 150 }}
            allowClear
            value={filters.credited_only}
            onChange={(value) => setFilters({ ...filters, credited_only: value, skip: 0 })}
            options={[
              { label: 'Credited Only', value: true },
              { label: 'Uncredited Only', value: false },
            ]}
          />
          <RangePicker
            value={dateRange}
            onChange={(dates) => setDateRange(dates as [dayjs.Dayjs | null, dayjs.Dayjs | null])}
          />
          <Button icon={<ReloadOutlined />} onClick={() => refetch()}>
            Refresh
          </Button>
        </Space>
      </Card>

      {/* Table */}
      <Card>
        <Table
          columns={columns}
          dataSource={paymentsData?.payments || []}
          rowKey="id"
          loading={isLoading}
          pagination={{
            total: paymentsData?.total || 0,
            pageSize: filters.limit,
            current: Math.floor(filters.skip / filters.limit) + 1,
            onChange: (page, pageSize) => {
              setFilters({
                ...filters,
                skip: (page - 1) * pageSize,
                limit: pageSize,
              });
            },
            showTotal: (total) => `Total ${total} payments`,
          }}
          rowClassName={(record) => !record.credited && record.status === 'finished' ? 'uncredited-payment' : ''}
        />
      </Card>

      <style>
        {`
          .uncredited-payment {
            background-color: #fff7e6;
          }
        `}
      </style>
    </div>
  );
};

export default Payments;








